import BaseService from "@/services/BaseService.ts";
import {supabase} from "@supabaseClient";
import { DerivativeSecurity } from "@/integrations/supabase/derivative.models";
import securitiesTransactionService from "@securitiesTransactionService";
import {calculateDaysUntilExpiration, isNearExpiry} from "@securityUtils";
import {
    DerivativeSecurityWithMetrics, DerivativeSecurityWithProfitLoss
} from "./types";
import {calculateFuturesProfitLoss, calculateOptionProfitLoss} from "@derivativeSecurityUtils";

/**
 * Derivative Security Service manages derivative securities
 */
class DerivativeSecurityService extends BaseService {
    /**
     * Fetches all derivative securities for the given user
     * @param {string} userId
     */
    async fetchDerivativeSecurities(userId: string): Promise<DerivativeSecurityWithMetrics[]> {
        const { data, error } = await supabase
            .from('derivative_securities')
            .select('*')
            .eq('user_id', userId)
            .order('created_at', { ascending: false });

        if (error) {
            await this.handleError(error)
            throw error;
        }

        return data.map(security => ({
            ...security,
            daysUntilExpiration: calculateDaysUntilExpiration(security.expiration_date),
            isNearExpiry: isNearExpiry(security.expiration_date),
        }));
    }

    /**
     * Fetches derivative security information by the request provided and includes profit loss and option calculations
     * Including transactions
     * @param {DerivativeSecurityFetchByIdRequest} request
     */
    async fetchDerivativeSecurityById(request: DerivativeSecurityFetchByIdRequest): Promise<DerivativeSecurityWithProfitLoss> {
        const { id, userId, currentUnderlyingPrice } = request;

        const { data: security, error: securityError } = await supabase
            .from('derivative_securities')
            .select('*')
            .eq('id', id)
            .eq('user_id', userId)
            .single();

        if (securityError) {
            await this.handleError(securityError);
            throw securityError;
        }

        const transactions = await securitiesTransactionService.fetchSecurityTransactionsForSecurity({
            securityId: id,
            userId,
            securityType: 'derivative'
        });

        const days_until_expiration = calculateDaysUntilExpiration(security.expiration_date);
        const is_near_expiry = isNearExpiry(security.expiration_date);

        let profitLossData = {} as ProfitLossResult;
        if (currentUnderlyingPrice !== undefined) {
            if (security.derivative_type === 'option' && security.option_type &&
                security.strike_price && security.premium_paid) {
                profitLossData = calculateOptionProfitLoss(
                    security.option_type as DerivativeOptionType,
                    security.strike_price,
                    security.premium_paid,
                    currentUnderlyingPrice,
                    security.contract_size
                );
            } else if (security.derivative_type === 'future' && security.contract_price) {
                profitLossData = calculateFuturesProfitLoss(
                    security.contract_price,
                    currentUnderlyingPrice,
                    security.contract_size
                );
            }
        }

        return {
            ...security,
            daysUntilExpiration: days_until_expiration,
            isNearExpiry: is_near_expiry,
            ...profitLossData,
            transactions: transactions || [],
        }
    }


        /**
     * Creates a debt security for a given user.
     * @param {DebtSecurityCreateRequest} request request to create a debt security
     */
    async createDerivativeSecurity(request: DerivativeSecurityCreateRequest): Promise<DerivativeSecurity> {
        const payload = this.convertRequestToSnakeCase(request);
        const { data, error } = await supabase
            .from('derivative_securities')
            .insert({ ...payload, user_id: request.userId })
            .select()
            .single();

        if (error) {
            await this.handleError(error);
            throw error;
        }

        return data
    }

    async updateDerivativeSecurity(request: DerivativeSecurityUpdateRequest): Promise<DerivativeSecurity> {
        const payload = this.convertRequestToSnakeCase(request);
        const { data, error } = await supabase
            .from('derivative_securities')
            .update(payload)
            .eq('id', payload.id)
            .eq("user_id", payload.user_id)
            .select()
            .single();

        if (error) {
            await this.handleError(error);
            throw error;
        }

        return data
    }

    /**
     * Deletes a derivative security for a given user.
     * @param {DerivativeSecurityDeleteRequest} request request to delete a derivative security
     */
    async deleteDerivativeSecurity(request: DerivativeSecurityDeleteRequest): Promise<void> {
        const { id, userId } = request;
        const { error } = await supabase
            .from('derivative_securities')
            .delete()
            .eq('id', id)
            .eq('user_id', userId);

        if (error) {
            await this.handleError(error);
            throw error;
        }
    }
}

export default new DerivativeSecurityService();